// Редактор модификаторов: группы, опции, привязка к блюдам.
// Правило группы (обязательность и лимит) задаётся при создании,
// дальше жизнь группы — переименование, опции, архив.
import { useState } from 'react';
import { useAskSheet, useNote } from './ui/ask';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminModifierGroups } from '@dastarhan/contracts';
import { formatMoney } from '@dastarhan/core';
import { z } from 'zod';

const API = '/api/v1';

const AdminMenuLite = z.object({
  products: z.array(z.object({
    id: z.string(), name: z.string(), isActive: z.boolean(),
    modifierGroupIds: z.array(z.string()),
  })),
});

const RULES = [
  { label: 'Обязательно один', min: 1, max: 1 },
  { label: 'По желанию, один', min: 0, max: 1 },
  { label: 'По желанию, до двух', min: 0, max: 2 },
  { label: 'По желанию, до трёх', min: 0, max: 3 },
] as const;

function ruleLabel(min: number, max: number): string {
  return RULES.find((r) => r.min === min && r.max === max)?.label
    ?? `от ${min} до ${max}`;
}

export function ModifiersScreen({ token }: { token: string }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  const qc = useQueryClient();
  const h = { Authorization: `Bearer ${token}` };
  const jh = { ...h, 'Content-Type': 'application/json' };
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['mod-groups'] });
    void qc.invalidateQueries({ queryKey: ['admin-menu'] });
    void qc.invalidateQueries({ queryKey: ['admin-menu-lite'] });
  };

  const groups = useQuery({
    queryKey: ['mod-groups'],
    queryFn: async () =>
      AdminModifierGroups.parse(await (await fetch(`${API}/menu/modifier-groups`, { headers: h })).json()),
  });
  const menu = useQuery({
    queryKey: ['admin-menu-lite'],
    queryFn: async () =>
      AdminMenuLite.parse(await (await fetch(`${API}/menu/admin`, { headers: h })).json()),
  });

  const createGroup = useMutation({
    mutationFn: (b: { name: string; minSelect: number; maxSelect: number }) =>
      fetch(`${API}/menu/modifier-groups`, { method: 'POST', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
  const patchGroup = useMutation({
    mutationFn: ({ id, ...b }: { id: string; name?: string; isActive?: boolean }) =>
      fetch(`${API}/menu/modifier-groups/${id}`, { method: 'PATCH', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
  const addOption = useMutation({
    mutationFn: ({ groupId, ...b }: { groupId: string; name: string; priceDelta: number }) =>
      fetch(`${API}/menu/modifier-groups/${groupId}/options`, { method: 'POST', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
  const patchOption = useMutation({
    mutationFn: ({ id, ...b }: { id: string; name?: string; priceDelta?: number; cost?: number; removesProductId?: string | null; removesQty?: number | null; isActive?: boolean }) =>
      fetch(`${API}/menu/options/${id}`, { method: 'PATCH', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
  const toggleLink = useMutation({
    mutationFn: (b: { productId: string; groupId: string; on: boolean }) =>
      fetch(`${API}/menu/products/${b.productId}/modifier-groups`, {
        method: 'POST', headers: jh, body: JSON.stringify({ groupId: b.groupId, on: b.on }),
      }),
    onSuccess: refresh,
  });

  const [name, setName] = useState('');
  const [rule, setRule] = useState(0);
  const [optDraft, setOptDraft] = useState<{ groupId: string; name: string; delta: string } | null>(null);

  if (groups.isPending || menu.isPending) return <p className="empty">Загружаем…</p>;
  if (groups.isError || menu.isError || !groups.data || !menu.data) {
    return <p className="empty">Не удалось загрузить. Обновите страницу.</p>;
  }

  const products = menu.data.products.filter((p) => p.isActive);

  return (
    <div className="mods-edit">
      <form className="menu-toolbar" onSubmit={(e) => {
        e.preventDefault();
        const r = RULES[rule]!;
        if (name.trim()) {
          createGroup.mutate({ name: name.trim(), minSelect: r.min, maxSelect: r.max });
          setName('');
        }
      }}>
        <div className="row">
          <input placeholder="Новая группа: «Острота», «Добавки»…"
            value={name} onChange={(e) => setName(e.target.value)} />
          <select value={rule} onChange={(e) => setRule(Number(e.target.value))}>
            {RULES.map((r, i) => <option key={r.label} value={i}>{r.label}</option>)}
          </select>
          <button className="btn" type="submit" disabled={!name.trim()}>Создать</button>
        </div>
      </form>

      {groups.data.length === 0 && (
        <p className="empty">Группа — это вопрос кассиру: «Какая острота?», «Что добавить?». Создайте первую.</p>
      )}

      {groups.data.map((g) => {
        // Список групп у товара может не прийти: старый кэш браузера,
        // оборванный ответ, ручная правка данных. Пустой список — рабочее
        // состояние («групп нет»), а падение экрана — нет
        const groupsOf = (x: { modifierGroupIds?: string[] }) => x.modifierGroupIds ?? [];
        const linked = products.filter((p) => groupsOf(p).includes(g.id));
        const free = products.filter((p) => !groupsOf(p).includes(g.id));
        return (
          <section key={g.id} className={`menu-cat ${g.isActive ? '' : 'cat-archived'}`}>
            <header>
              <button className="name-btn" title="Переименовать"
                onClick={() => void (async () => {
                  const a = await ask({
                    title: 'Название группы',
                    sub: 'Так группа называется на кассе, когда официант выбирает добавки.',
                    value: { label: 'Как называется', initial: g.name },
                    confirmLabel: 'Переименовать',
                  });
                  if (a) patchGroup.mutate({ id: g.id, name: a.value });
                })()}>
                <h2>{g.name}{!g.isActive && ' · в архиве'}</h2>
              </button>
              <span className="rule-tag">{ruleLabel(g.minSelect, g.maxSelect)}</span>
              <button className="btn small"
                onClick={() => patchGroup.mutate({ id: g.id, isActive: !g.isActive })}>
                {g.isActive ? 'В архив' : 'Вернуть'}
              </button>
            </header>

            {g.options.map((o) => (
              <div key={o.id} className={`menu-row ${o.isActive ? '' : 'archived'}`}>
                <span className="name">{o.name}{!o.isActive && ' · в архиве'}</span>
                <button className="price money" title="Изменить надбавку"
                  onClick={() => void (async () => {
                    const a = await ask({
                      title: `Надбавка за «${o.name}»`,
                      sub: 'Прибавляется к цене блюда, когда гость выбирает эту добавку.',
                      effects: [
                        ['Сейчас', o.priceDelta === 0 ? 'бесплатно' : formatMoney(o.priceDelta)],
                        ...(o.cost ? [['Себестоимость', formatMoney(o.cost)] as [string, string]] : []),
                      ],
                      value: {
                        label: 'Надбавка, ₸',
                        initial: String(o.priceDelta / 100),
                        numeric: true,
                        hint: 'ноль — добавка бесплатна для гостя',
                      },
                      confirmLabel: 'Сохранить',
                    });
                    if (!a) return;
                    const priceDelta = Math.round(Number(a.value.replace(',', '.')) * 100);
                    if (Number.isFinite(priceDelta) && priceDelta >= 0) {
                      patchOption.mutate({ id: o.id, priceDelta });
                    } else note('Надбавка не может быть отрицательной', 'bad');
                  })()}>
                  {o.priceDelta === 0 ? 'бесплатно' : `+${formatMoney(o.priceDelta)}`}
                </button>
                <button className="btn small" title="Себестоимость добавки — попадает в фудкост"
                  onClick={() => void (async () => {
                    const a = await ask({
                      title: `Себестоимость «${o.name}»`,
                      sub: 'Попадает в фудкост и в расчёт маржи по блюду.',
                      effects: [['Надбавка для гостя', o.priceDelta === 0 ? 'бесплатно' : formatMoney(o.priceDelta)]],
                      value: {
                        label: 'Себестоимость, ₸',
                        initial: String((o.cost ?? 0) / 100),
                        numeric: true,
                        hint: 'сколько добавка стоит заведению',
                      },
                      confirmLabel: 'Сохранить',
                    });
                    if (!a) return;
                    const cost = Math.round(Number(a.value.replace(',', '.')) * 100);
                    if (Number.isFinite(cost) && cost >= 0) patchOption.mutate({ id: o.id, cost });
                    else note('Себестоимость не может быть отрицательной', 'bad');
                  })()}>
                  себ. {formatMoney(o.cost ?? 0)}
                </button>
                <button className="btn small"
                  title="«Без лука»: какой ингредиент и сколько НЕ спишется со склада"
                  onClick={() => void (async () => {
                    /* Ингредиент выбирается из списка, а не набирается руками:
                       раньше несовпадение по букве давало «не нашли», а связь
                       со складом молча не создавалась. */
                    const all = menu.data?.products ?? [];
                    if (all.length === 0) {
                      note('Меню ещё не загрузилось — попробуйте через секунду', 'bad');
                      return;
                    }
                    /* сервер отдаёт связь объектом removes: { productId, qty } */
                    const linked = all.find((pp) => pp.id === o.removes?.productId);
                    const a = await ask({
                      title: `Что не списывать при «${o.name}»`,
                      sub: 'Гость просит без лука — значит лук не должен уйти со склада. Выберите ингредиент и сколько его не расходуется.',
                      ...(linked ? { effects: [['Сейчас связано с', linked.name]] as [string, string][] } : {}),
                      choices: {
                        label: 'Ингредиент',
                        initial: o.removes?.productId ?? 'none',
                        options: [
                          { id: 'none', title: 'Убрать связь', note: 'списывать всё как обычно' },
                          ...all.slice(0, 60).map((pp) => ({ id: pp.id, title: pp.name })),
                        ],
                      },
                      value: {
                        label: 'Сколько не уходит, в единицах учёта',
                        initial: o.removes?.qty ? String(o.removes.qty) : '0.05',
                        numeric: true,
                        allowEmpty: true,
                        hint: 'при «Убрать связь» число не используется',
                      },
                      confirmLabel: 'Сохранить',
                    });
                    if (!a) return;
                    if (a.choice === 'none') {
                      patchOption.mutate({ id: o.id, removesProductId: null, removesQty: null });
                      note('Связь со складом убрана');
                      return;
                    }
                    const qty = Number(a.value.replace(',', '.'));
                    if (!Number.isFinite(qty) || qty <= 0) {
                      note('Количество должно быть больше нуля', 'bad');
                      return;
                    }
                    patchOption.mutate({ id: o.id, removesProductId: a.choice, removesQty: qty });
                  })()}>
                  ➖ ингредиент
                </button>
                <button className="btn small"
                  onClick={() => patchOption.mutate({ id: o.id, isActive: !o.isActive })}>
                  {o.isActive ? 'В архив' : 'Вернуть'}
                </button>
              </div>
            ))}

            {optDraft?.groupId === g.id ? (
              <form className="menu-row draft" onSubmit={(e) => {
                e.preventDefault();
                const delta = Math.round(Number(optDraft.delta.replace(',', '.') || '0') * 100);
                if (optDraft.name.trim() && delta >= 0) {
                  addOption.mutate({ groupId: g.id, name: optDraft.name.trim(), priceDelta: delta });
                  setOptDraft(null);
                }
              }}>
                <input autoFocus placeholder="Вариант: «Острый», «Казы»…" value={optDraft.name}
                  onChange={(e) => setOptDraft({ ...optDraft, name: e.target.value })} />
                <input placeholder="Надбавка, ₸ (0)" inputMode="decimal" value={optDraft.delta}
                  onChange={(e) => setOptDraft({ ...optDraft, delta: e.target.value })} />
                <button className="btn small" type="submit">Сохранить</button>
                <button className="btn small" type="button" onClick={() => setOptDraft(null)}>Отмена</button>
              </form>
            ) : (
              <div className="menu-row">
                <button className="btn small" onClick={() => setOptDraft({ groupId: g.id, name: '', delta: '' })}>
                  + Вариант
                </button>
              </div>
            )}

            <div className="link-row">
              <span className="link-label">Применяется к:</span>
              {linked.length === 0 && <span className="cat-empty-inline">пока ни к одному блюду</span>}
              {linked.map((p) => (
                <button key={p.id} className="chip" title="Отвязать"
                  onClick={() => toggleLink.mutate({ productId: p.id, groupId: g.id, on: false })}>
                  {p.name} ✕
                </button>
              ))}
              {free.length > 0 && (
                <select value="" aria-label="Привязать блюдо"
                  onChange={(e) => {
                    if (e.target.value) toggleLink.mutate({ productId: e.target.value, groupId: g.id, on: true });
                  }}>
                  <option value="">+ блюдо…</option>
                  {free.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
            </div>
          </section>
        );
      })}
      {sheet}
      {noteView}
    </div>
  );
}
